import { Injectable } from '@angular/core';
import { UserPreference } from '@ng-cloud/badger-core/models/user-preference';
import { Observable, ReplaySubject } from 'rxjs';
import { AuthorizationService } from './authorization.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  static readonly DEFAULT_THEME = 'badger-light';

  private currentTheme: string;
  private themeSource = new ReplaySubject<string>(1);
  private userPreference: UserPreference;

  constructor(
    private authService: AuthorizationService,
    private userService: UserService) {

    this.authService.getCurrentUser().subscribe(currentUser => {
      this.userService.getUser(currentUser.id).subscribe(user => {

        this.userService.getOrCreateUserPreference(user).subscribe(preference => {
          this.userPreference = preference;

          if (preference.preferences['theme']) {
            this.currentTheme = preference.preferences['theme'];
          }
          else {
            this.currentTheme = ThemeService.DEFAULT_THEME;
          }

          this.setCurrentTheme(this.currentTheme);
        });

        this.userService.userPreferences(user.id).subscribe(pref => {
          if (this.userPreference) {
            this.userPreference.preferences = pref.preferences;
          }

          if (pref.preferences && pref.preferences['theme']) {
            if (this.currentTheme !== pref.preferences['theme']) {
              this.currentTheme = pref.preferences['theme'];
              this.setCurrentTheme(this.currentTheme);
            }
          }
        });
      });
    });
  }

  themes(): Observable<string> {
    return this.themeSource.asObservable();
  }

  getCurrentTheme(): string {
    return this.currentTheme;
  }

  toggleTheme() {
    const userPref = this.userPreference;
    userPref.preferences['theme'] = this.currentTheme === 'badger-light' ? 'badger-dark' : 'badger-light';
    this.userService.updateUserPreference(userPref).subscribe();
  }

  setCurrentTheme(theme: string): void {
    this.currentTheme = theme;
    this.setStyle('theme', `${this.currentTheme}.css`);
    this.themeSource.next(this.currentTheme);
  }

  isDarkTheme(theme: string): boolean {
    return theme && theme.toLowerCase().includes('dark');
  }

  setStyle(key: string, href: string) {
    getLinkElementForKey(key).setAttribute('href', href);
  }
}

function getLinkElementForKey(key: string) {
  return getExistingLinkElementByKey(key) || createLinkElementWithKey(key);
}

function getExistingLinkElementByKey(key: string) {
  return document.head.querySelector(`link[rel="stylesheet"].${getClassNameForKey(key)}`);
}

function createLinkElementWithKey(key: string) {
  const linkEl = document.createElement('link');
  linkEl.setAttribute('rel', 'stylesheet');
  linkEl.classList.add(getClassNameForKey(key));
  document.head.appendChild(linkEl);
  return linkEl;
}

function getClassNameForKey(key: string) {
  return `badger-style-${key}`;
}

